import React, { useState } from 'react';
import { ChevronLeft, Mail, Bug } from 'lucide-react';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
interface ForgotPasswordProps {
  onBack: () => void;
  onEmailSent: () => void;
  onCreateNewPassword?: () => void;
  onLinkExpired?: () => void;
  onResetSuccess?: () => void;
}
export function ForgotPassword({
  onBack,
  onEmailSent,
  onCreateNewPassword,
  onLinkExpired,
  onResetSuccess
}: ForgotPasswordProps) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [showDebug, setShowDebug] = useState(false);
  const validateEmail = (value: string): string => {
    if (!value) return 'Enter your email address';
    if (!/\S+@\S+\.\S+/.test(value)) return 'Enter a valid email address';
    return '';
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validateEmail(email);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    onEmailSent();
  };
  const loadDebugState = (state: string) => {
    setShowDebug(false);
    setError('');
    if (state === 'invalid-email') {
      setEmail('invalid-email');
      setError('Enter a valid email address');
    } else if (state === 'empty') {
      setEmail('');
    }
  };
  return <div className="flex flex-col h-full w-full bg-stone-50 overflow-y-auto relative" data-id="element-729">
      {/* Debug Toggle */}
      <button onClick={() => setShowDebug(!showDebug)} className="absolute top-8 right-2 z-40 w-7 h-7 rounded-full bg-slate-800/70 text-white flex items-center justify-center hover:bg-slate-800 transition-colors" title="Debug panel" data-id="element-730">
        <Bug size={14} data-id="element-731" />
      </button>

      {/* Debug Panel */}
      {showDebug && <div className="absolute top-16 right-2 z-40 bg-slate-800 text-white rounded-2xl p-3 shadow-xl w-64 text-xs animate-in fade-in zoom-in-95 duration-150 max-h-[500px] overflow-y-auto" data-id="element-732">
          <p className="font-bold text-slate-400 uppercase tracking-wider text-[10px] mb-2" data-id="element-733">
            Debug Panel
          </p>
          <button onClick={() => loadDebugState('empty')} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-734">
            ▶ Empty state
          </button>
          <button onClick={() => loadDebugState('invalid-email')} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-735">
            ▶ Invalid email error
          </button>

          <div className="border-t border-slate-700 pt-2 mt-2 mb-1" data-id="element-736">
            <p className="font-bold text-slate-400 uppercase tracking-wider text-[10px] mb-2" data-id="element-737">
              Jump to Flow States
            </p>
            <button onClick={() => {
          setShowDebug(false);
          onCreateNewPassword?.();
        }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-738">
              ▶ Create New Password
            </button>
            <button onClick={() => {
          setShowDebug(false);
          onLinkExpired?.();
        }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-739">
              ▶ Link Expired state
            </button>
            <button onClick={() => {
          setShowDebug(false);
          onResetSuccess?.();
        }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium" data-id="element-740">
              ▶ Password Reset Success
            </button>
          </div>
        </div>}

      {/* Header */}
      <div className="flex items-center justify-between p-6 pt-8 sticky top-0 bg-stone-50/90 backdrop-blur-md z-20" data-id="element-741">
        <button onClick={onBack} className="p-2 -ml-2 rounded-full hover:bg-slate-200/50 text-slate-600 transition-colors" aria-label="Go back" data-id="element-742">
          <ChevronLeft size={28} data-id="element-743" />
        </button>
        <div className="w-12 h-12 rounded-2xl overflow-hidden border-2 border-white shadow-sm bg-stone-200" data-id="element-744">
          <video autoPlay loop muted playsInline className="w-full h-full object-cover" src="https://cdn.magicpatterns.com/uploads/69mUG6ooMsKbP89Ta37mkt/Katethought_friendly_gym_mascot_character_funny_good_guy_musc_bc3cfd10-111a-4766-8e76-81af2852edc8_2.mp4" data-id="element-745" />
        </div>
        <div className="w-10" data-id="element-746" />
      </div>

      <div className="px-6 pb-8 flex-1 flex flex-col" data-id="element-747">
        {/* Title Area */}
        <div className="mb-8 mt-2" data-id="element-748">
          <h1 className="text-3xl font-extrabold text-slate-800 mb-2" data-id="element-749">
            Reset your password
          </h1>
          <p className="text-slate-500 font-medium leading-relaxed" data-id="element-750">
            Enter the email address linked to your account and we'll send you a
            reset link.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5 flex-1" data-id="element-751">
          <Input label="Email Address" icon={Mail} type="email" placeholder="flex@gymbuddy.app" value={email} onChange={e => {
          setEmail(e.target.value);
          setError('');
        }} error={error} data-id="element-752" />

          <div className="pt-6 space-y-4" data-id="element-753">
            <Button type="submit" variant="primary" fullWidth data-id="element-754">
              Send reset link
            </Button>

            <button type="button" onClick={onBack} className="w-full text-center text-sm font-bold text-slate-600 hover:text-slate-800 py-2" data-id="element-755">
              Back to Login
            </button>
          </div>
        </form>
      </div>
    </div>;
}